import { useState } from 'react';

import { Plus, Trash2, X } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

import type { SubItemConfig, TreeNodeConfig } from '@repo/shared-types';

const COLOR_OPTIONS = ['blue', 'green', 'teal', 'amber', 'red', 'purple', 'slate', 'orange'];
const COLOR_PREVIEW: Record<string, string> = {
  blue: '#3b82f6',
  green: '#10b981',
  teal: '#14b8a6',
  amber: '#f59e0b',
  red: '#f43f5e',
  purple: '#8b5cf6',
  slate: '#64748b',
  orange: '#f97316',
};

function ColorPicker({ value, onChange }: { value: string; onChange: (c: string) => void }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {COLOR_OPTIONS.map((c) => (
        <button
          key={c}
          type="button"
          title={c}
          onClick={() => onChange(c)}
          className={`h-6 w-6 rounded-full border-2 transition-all ${
            value === c ? 'border-foreground scale-110' : 'border-transparent'
          }`}
          style={{ backgroundColor: COLOR_PREVIEW[c] }}
        />
      ))}
    </div>
  );
}

function SubItemsEditor({
  items,
  onChange,
  metrics,
}: {
  items: SubItemConfig[];
  onChange: (items: SubItemConfig[]) => void;
  metrics: string[];
}) {
  const addItem = () => {
    onChange([
      ...items,
      {
        label: '',
        metricKey: '',
        classification: '',
        colorClass: 'slate',
        parentMetricKey: '',
        pctBaseMetricKey: '',
      },
    ]);
  };

  const updateItem = (i: number, patch: Partial<SubItemConfig>) => {
    onChange(items.map((item, idx) => (idx === i ? { ...item, ...patch } : item)));
  };

  const removeItem = (i: number) => {
    onChange(items.filter((_, idx) => idx !== i));
  };

  return (
    <div className="mt-4 ml-2 space-y-3 border-l-2 border-dashed pl-3">
      <p className="text-muted-foreground text-[10px] font-bold tracking-wider uppercase">
        Sub-items
      </p>
      {items.map((item, i) => (
        <div key={i} className="space-y-2 rounded-lg border border-dashed p-2">
          <div className="flex items-center gap-2">
            <Input
              value={item.label}
              onChange={(e) => updateItem(i, { label: e.target.value })}
              placeholder="Label"
              className="h-8 flex-1 text-xs"
            />
            <Button
              variant="ghost"
              size="icon"
              className="text-destructive h-8 w-8"
              onClick={() => removeItem(i)}
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-col gap-2">
            <select
              value={item.metricKey}
              onChange={(e) => updateItem(i, { metricKey: e.target.value })}
              className="bg-background h-8 rounded border px-2 text-xs"
            >
              <option value="">Metric...</option>
              {metrics.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            {/* Classification dropdown hidden under v2 — the drilldown
                resolves via metricKey through MetricResolver. */}
            <select
              value={item.pctBaseMetricKey}
              onChange={(e) => updateItem(i, { pctBaseMetricKey: e.target.value })}
              className="bg-background h-8 rounded border px-2 text-xs"
            >
              <option value="">% Base Metric...</option>
              {metrics.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
            <ColorPicker
              value={item.colorClass}
              onChange={(c) => updateItem(i, { colorClass: c })}
            />
          </div>
        </div>
      ))}
      <Button
        variant="outline"
        size="sm"
        className="h-7 w-full border-dashed text-xs"
        onClick={addItem}
      >
        <Plus className="mr-1 h-3 w-3" />
        Add Sub-item
      </Button>
    </div>
  );
}

export function NodeEditorSidebar({
  node,
  metrics,
  isOpen,
  onUpdate,
  onDelete,
  onClose,
}: {
  node: TreeNodeConfig | null;
  metrics: string[];
  isOpen: boolean;
  onUpdate: (id: string, patch: Partial<TreeNodeConfig>) => void;
  onDelete: (id: string) => void;
  onClose: () => void;
}) {
  const [cachedNode, setCachedNode] = useState<TreeNodeConfig | null>(node);
  const [prevNode, setPrevNode] = useState<TreeNodeConfig | null>(node);

  if (node !== prevNode) {
    setPrevNode(node);
    if (node) {
      setCachedNode(node);
    }
  }

  const displayNode = node || cachedNode;

  return (
    <div
      className={cn(
        'relative z-50 flex h-full shrink-0 flex-col overflow-y-clip border-l border-transparent transition-[width,transform] duration-300 ease-in-out',
        isOpen
          ? 'w-[320px] translate-x-0'
          : 'pointer-events-none w-0 translate-x-full border-l-transparent',
      )}
    >
      <div className="bg-card absolute top-0 left-0 flex h-full w-[320px] flex-col overflow-hidden border-l shadow-[-10px_0_30px_-15px_rgba(0,0,0,0.3)]">
        {displayNode && (
          <>
            <div className="bg-background/95 sticky top-0 z-10 flex shrink-0 items-center justify-between border-b p-4 backdrop-blur">
              <h3 className="text-sm font-semibold">Edit Node</h3>
              <Button
                variant="ghost"
                size="icon"
                className="text-muted-foreground h-8 w-8"
                onClick={onClose}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto p-4">
              <div className="flex items-center gap-2">
                <div
                  className="h-3 w-3 shrink-0 rounded-full"
                  style={{ backgroundColor: COLOR_PREVIEW[displayNode.colorClass] ?? '#64748b' }}
                />
                <span className="flex-1 truncate text-sm font-bold">
                  {displayNode.label || displayNode.id}
                </span>
                {displayNode.complementary && (
                  <Badge
                    variant="outline"
                    className="border-amber-500/40 text-[10px] text-amber-500"
                  >
                    Auto Complementary
                  </Badge>
                )}
              </div>

              {/* Complementary toggle */}
              <label className="bg-muted/20 mt-4 flex cursor-pointer items-center gap-2 rounded-lg border p-2">
                <input
                  type="checkbox"
                  checked={displayNode.complementary ?? false}
                  onChange={(e) =>
                    onUpdate(displayNode.id, {
                      complementary: e.target.checked || undefined,
                      metricKey: e.target.checked ? undefined : displayNode.metricKey,
                    })
                  }
                  className="accent-primary h-4 w-4"
                />
                <div className="flex flex-col">
                  <span className="text-sm font-medium">Complementary</span>
                  <span className="text-muted-foreground mt-1 text-[10px] leading-tight">
                    Count auto-resolves as parent − sum(siblings)
                  </span>
                </div>
              </label>

              <div className="space-y-3">
                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-semibold">
                    Node ID
                  </label>
                  <Input
                    value={displayNode.id}
                    onChange={(e) => onUpdate(displayNode.id, { id: e.target.value })}
                    className="h-8 font-mono text-xs"
                  />
                  <p className="text-muted-foreground mt-1 text-[10px]">
                    Warning: Changing ID breaks existing connections.
                  </p>
                </div>

                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-semibold">
                    Label
                  </label>
                  <Input
                    value={displayNode.label}
                    onChange={(e) => onUpdate(displayNode.id, { label: e.target.value })}
                    placeholder="Display name"
                    className="h-8 text-sm"
                  />
                </div>

                {!displayNode.complementary && (
                  <div className="bg-muted/20 space-y-2 rounded-lg border p-3">
                    <label className="text-muted-foreground mb-1 block text-xs font-semibold">
                      Bound Metric(s)
                    </label>
                    {(displayNode.metricKeys && displayNode.metricKeys.length > 0
                      ? displayNode.metricKeys
                      : [displayNode.metricKey ?? '']
                    ).map((mk: string, idx: number, arr: string[]) => (
                      <div key={idx} className="flex flex-col gap-1">
                        <div className="flex items-center gap-1">
                          <select
                            value={mk}
                            onChange={(e) => {
                              const val = e.target.value || undefined;
                              if (arr.length <= 1 && !displayNode.metricKeys?.length) {
                                onUpdate(displayNode.id, { metricKey: val });
                              } else {
                                const updated = [...(displayNode.metricKeys ?? [])];
                                updated[idx] = e.target.value;
                                onUpdate(displayNode.id, {
                                  metricKeys: updated.filter(Boolean),
                                  metricKey: undefined,
                                });
                              }
                            }}
                            className="bg-background h-8 flex-1 rounded-md border px-2 text-xs"
                          >
                            <option value="">Select metric...</option>
                            {metrics.map((m) => (
                              <option key={m} value={m}>
                                {m}
                              </option>
                            ))}
                          </select>
                          {displayNode.metricKeys && displayNode.metricKeys.length > 1 && (
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-destructive h-8 w-8"
                              onClick={() => {
                                const updated = displayNode.metricKeys!.filter(
                                  (_: string, i: number) => i !== idx,
                                );
                                if (updated.length <= 1) {
                                  onUpdate(displayNode.id, {
                                    metricKey: updated[0] || undefined,
                                    metricKeys: undefined,
                                    metricCombination: undefined,
                                  });
                                } else {
                                  onUpdate(displayNode.id, { metricKeys: updated });
                                }
                              }}
                            >
                              <X className="h-3 w-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-1 h-7 w-full border-dashed text-[10px]"
                      onClick={() => {
                        const existing = displayNode.metricKeys?.length
                          ? displayNode.metricKeys
                          : displayNode.metricKey
                            ? [displayNode.metricKey]
                            : [];
                        onUpdate(displayNode.id, {
                          metricKeys: [...existing, ''],
                          metricKey: undefined,
                        });
                      }}
                    >
                      <Plus className="mr-1 h-3 w-3" /> Add Metric Condition
                    </Button>
                    {displayNode.metricKeys && displayNode.metricKeys.length >= 2 && (
                      <div className="border-muted mt-2 border-t border-dashed pt-2">
                        <select
                          value={displayNode.metricCombination ?? 'union'}
                          onChange={(e) =>
                            onUpdate(displayNode.id, {
                              metricCombination: e.target.value as 'union' | 'intersection',
                            })
                          }
                          className="bg-background h-7 w-full rounded-md border px-2 text-xs"
                        >
                          <option value="union">Union (OR)</option>
                          <option value="intersection">Intersection (AND)</option>
                        </select>
                      </div>
                    )}
                  </div>
                )}

                {/* Classification Filter dropdown hidden under v2 — the
                    drilldown resolves via metricKey through MetricResolver. */}

                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-semibold">
                    Parent Metric (for %)
                  </label>
                  <select
                    value={displayNode.parentMetricKey ?? ''}
                    onChange={(e) =>
                      onUpdate(displayNode.id, { parentMetricKey: e.target.value || undefined })
                    }
                    className="bg-background h-8 w-full rounded-md border px-2 text-xs"
                  >
                    <option value="">None (root)</option>
                    {metrics.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="text-muted-foreground mb-1 block text-xs font-semibold">
                    Tooltip
                  </label>
                  <Input
                    value={displayNode.tooltip ?? ''}
                    onChange={(e) =>
                      onUpdate(displayNode.id, { tooltip: e.target.value || undefined })
                    }
                    placeholder="Hover description"
                    className="h-8 text-xs"
                  />
                </div>

                <div>
                  <label className="text-muted-foreground mb-1.5 block text-xs font-semibold">
                    Theme Color
                  </label>
                  <ColorPicker
                    value={displayNode.colorClass}
                    onChange={(c) => onUpdate(displayNode.id, { colorClass: c })}
                  />
                </div>

                <SubItemsEditor
                  items={displayNode.subItems ?? []}
                  onChange={(subItems) =>
                    onUpdate(displayNode.id, { subItems: subItems.length ? subItems : undefined })
                  }
                  metrics={metrics}
                />
              </div>
            </div>

            <div className="bg-muted/10 mt-auto shrink-0 border-t p-4">
              <Button
                variant="destructive"
                className="h-8 w-full text-xs"
                onClick={() => onDelete(displayNode.id)}
              >
                <Trash2 className="mr-2 h-3.5 w-3.5" />
                Delete Node
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
